import groupBy from "lodash.groupby";
import sortBy from "lodash.sortby";
import type { printOnDemand } from "common/utils/models";
import { BLANKS_SIZES } from "@design-lab/shared/constants/constants";

export type BasePrice = {
  value: number;
  sizes: printOnDemand.getProduct.Size[];
};

/**
 * Calculates the base price for a composition by finding the most common price among variants.
 *
 * The logic works as follows:
 * 1. In sample mode, only considers the L size
 * 2. Groups all variants by their price
 * 3. Finds the group(s) with the maximum number of variants (most common price)
 * 4. If multiple groups have the same maximum count, selects the group containing the smallest size
 * 5. Returns the price value and all sizes that have this base price
 *
 * @example
 * // If variants have prices: S=$10, M=$10, L=$10, XL=$15, XXL=$15
 * // Returns: { value: 10, sizes: ['S', 'M', 'L'] }
 * // Because $10 appears 3 times vs $15 appears 2 times
 */
export function calculateBasePrice(
  composition: printOnDemand.getProduct.Composition,
  getVariantPrice: (variantId: string) => string | undefined,
  isSample: boolean = false
): BasePrice | undefined {
  type VariantWithPrice = {
    id: string;
    size: printOnDemand.getProduct.Size;
    price: string;
  };

  // Get variants with prices, filtering out those without prices
  // In sample mode, only consider L size
  const variantsToConsider = isSample
    ? composition.variants.filter(v => v.size === "L")
    : composition.variants;

  const variantsWithPrices = variantsToConsider
    .map(variant => ({
      id: variant.id,
      size: variant.size,
      price: getVariantPrice(variant.id)
    }))
    .filter((variant): variant is VariantWithPrice => variant.price !== undefined);

  if (variantsWithPrices.length === 0) return undefined;

  // Group variants by price using lodash
  const priceGroups = groupBy(variantsWithPrices, "price");

  // Get size index for sorting (smaller sizes have priority)
  const getSizeIndex = (size: printOnDemand.getProduct.Size): number => {
    const index = BLANKS_SIZES.indexOf(size.toUpperCase() as printOnDemand.getProduct.Size);
    return index === -1 ? 999 : index; // Unknown sizes go to the end
  };

  // Convert to array of groups with metadata and sort by group size (desc) and smallest size (asc)
  const sortedGroups = sortBy(
    Object.entries(priceGroups).map(([price, variants]) => {
      const typedVariants = variants as VariantWithPrice[];
      return {
        price,
        variants: typedVariants,
        count: typedVariants.length,
        smallestSizeIndex: Math.min(...typedVariants.map(v => getSizeIndex(v.size)))
      };
    }),
    [
      group => -group.count, // Descending by count (most variants first)
      group => group.smallestSizeIndex // Ascending by smallest size index
    ]
  );

  const baseGroup = sortedGroups[0];
  if (!baseGroup) return undefined;

  return {
    value: parseFloat(baseGroup.price),
    sizes: baseGroup.variants.map(variant => variant.size)
  };
}
